"use client"

import React from 'react'
import { cn } from '@/shared/utils/tw'
import type { SupportedKioskLanguage } from '../types/kiosk-config'
import { CHAT_HISTORY_BUTTON_TEXT, CLOSE_BUTTON_TEXT } from '../types/kiosk-config'

/**
 * 키오스크 대화 기록 버튼
 *
 * Figma 디자인 (2026-02-11 업데이트):
 * - 위치: 좌측 하단 (x=130, y=3420 in 2160x3840)
 * - 기본 상태: 아이콘 + "대화 기록" 텍스트
 * - 모달 열린 상태: "닫기" 텍스트만, z-[301] (모달 위)
 * - Dimmed: opacity 0.5 (대화 없을 때)
 */

export interface KioskChatHistoryButtonProps {
  onClick: () => void
  /** 대화가 있는지 여부 (없으면 dimmed 처리) */
  hasMessages: boolean
  /** 선택된 언어 */
  selectedLanguage: SupportedKioskLanguage
  /** 대화기록 모달 열림 상태 */
  isChatHistoryOpen: boolean
}

// Figma 기준 사이즈 (2160 x 3840)
const FIGMA_WIDTH = 2160
const FIGMA_HEIGHT = 3840

export const KioskChatHistoryButton = ({
  onClick,
  hasMessages,
  selectedLanguage,
  isChatHistoryOpen,
}: KioskChatHistoryButtonProps) => {
  // 모달 열린 상태: "닫기" 버튼
  if (isChatHistoryOpen) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onClick()
        }}
        className={cn(
          'absolute flex items-center justify-center',
          // 모달 위 z-index
          'z-[301]',
          'transition-all',
          'active:scale-95',
          'font-pretendard font-bold text-white',
          'pointer-events-auto'
        )}
        style={{
          // 좌측 하단 위치: x=130 → 6.02%
          left: `${(130 / FIGMA_WIDTH) * 100}%`,
          bottom: `${((FIGMA_HEIGHT - 3433 - 200) / FIGMA_HEIGHT) * 100}%`,
          // 패딩
          padding: `${(48 / FIGMA_HEIGHT) * 100}cqh ${(60 / FIGMA_WIDTH) * 100}cqw`,
          // 배경색 (Figma 모달 열림: rgba(255, 255, 255, 0.1))
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          // 강조 테두리 (Figma: 10px, rgba(255, 255, 255, 0.8))
          border: `${(10 / FIGMA_WIDTH) * 100}cqw solid rgba(255, 255, 255, 0.8)`,
          borderRadius: '999px',
          // 텍스트 (Figma: 70px, fontWeight 700, letterSpacing 4%)
          fontSize: `${(70 / FIGMA_WIDTH) * 100}cqw`,
          letterSpacing: '0.04em',
          whiteSpace: 'nowrap',
        }}
      >
        {CLOSE_BUTTON_TEXT[selectedLanguage]}
      </button>
    )
  }

  // 기본 상태: "대화 기록" 버튼 (아이콘 + 텍스트)
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        if (hasMessages) {
          onClick()
        }
      }}
      className={cn(
        'absolute z-50 flex items-center justify-center',
        'transition-all',
        hasMessages && 'active:scale-95',
        'font-pretendard font-semibold text-white',
        'pointer-events-auto'
      )}
      style={{
        // 좌측 하단 위치: x=130 → 6.02%
        left: `${(130 / FIGMA_WIDTH) * 100}%`,
        bottom: `${((FIGMA_HEIGHT - 3420 - 200) / FIGMA_HEIGHT) * 100}%`,
        // 패딩
        padding: `${(48 / FIGMA_HEIGHT) * 100}cqh ${(60 / FIGMA_WIDTH) * 100}cqw`,
        // gap (아이콘과 텍스트 사이)
        gap: `${(20 / FIGMA_WIDTH) * 100}cqw`,
        // 배경색 (Figma: rgba(255, 255, 255, 0.15))
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        // 테두리 (Figma: 10px, rgba(255, 255, 255, 0.2))
        border: `${(10 / FIGMA_WIDTH) * 100}cqw solid rgba(255, 255, 255, 0.2)`,
        borderRadius: '999px',
        // 텍스트 (Figma: 72px, fontWeight 600, letterSpacing 4%)
        fontSize: `${(72 / FIGMA_WIDTH) * 100}cqw`,
        letterSpacing: '0.04em',
        whiteSpace: 'nowrap',
        // Dimmed 처리: 대화 없으면 opacity 0.5
        opacity: hasMessages ? 1 : 0.5,
        pointerEvents: hasMessages ? 'auto' : 'none',
      }}
    >
      {/* 대화 기록 아이콘 (text icon - stroke only) */}
      <ChatHistoryIcon />
      {CHAT_HISTORY_BUTTON_TEXT[selectedLanguage]}
    </button>
  )
}

/**
 * 대화 기록 아이콘 (텍스트/문서 아이콘)
 *
 * Figma: 100x100 영역, stroke 8px white
 */
const ChatHistoryIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100" fill="none" style={{
      width: `${(100 / FIGMA_WIDTH) * 100}cqw`,
      height: `${(100 / FIGMA_WIDTH) * 100}cqw`,
      minWidth: '20px',
      minHeight: '20px',
      flexShrink: 0,
    }}>
      <path d="M49.999 16.6719L49.999 12.6719H49.999V16.6719ZM87.498 50.0049L91.498 50.0049V50.0049L87.498 50.0049ZM49.999 83.3389L48.2102 86.9166C48.7656 87.1943 49.378 87.3389 49.999 87.3389L49.999 83.3389ZM49.9971 83.3379L51.7859 79.7602C50.0312 78.8828 47.8992 79.4075 46.7521 80.999C45.605 82.5906 45.7814 84.7791 47.1686 86.1663L49.9971 83.3379ZM49.998 83.3389V87.3389C51.6159 87.3389 53.0744 86.3643 53.6936 84.8696C54.3127 83.3749 53.9705 81.6544 52.8265 80.5104L49.998 83.3389ZM12.5 83.3389L9.24743 81.0106C8.37487 82.2296 8.25712 83.8341 8.9424 85.1674C9.62767 86.5007 11.0009 87.3389 12.5 87.3389V83.3389ZM21.1318 71.2803L24.3844 73.6085C25.4934 72.0593 25.3557 69.9429 24.0555 68.5504L21.1318 71.2803ZM12.5 50.0049L8.5 50.0049V50.0049L12.5 50.0049ZM49.999 16.6719L49.999 20.6719C68.9557 20.6719 83.4979 34.2345 83.498 50.0049L87.498 50.0049L91.498 50.0049C91.4979 28.9565 72.4623 12.6719 49.999 12.6719L49.999 16.6719ZM87.498 50.0049L83.498 50.0049C83.498 65.7755 68.9556 79.3388 49.999 79.3389L49.999 83.3389L49.999 87.3389C72.4625 87.3388 91.498 71.0531 91.498 50.0049L87.498 50.0049ZM49.999 83.3389L51.7879 79.7612L51.7859 79.7602L49.9971 83.3379L48.2082 86.9156L48.2102 86.9166L49.999 83.3389ZM49.9971 83.3379L47.1686 86.1663L47.1696 86.1673L49.998 83.3389L52.8265 80.5104L52.8255 80.5095L49.9971 83.3379ZM49.998 83.3389V79.3389H12.5V83.3389V87.3389H49.998V83.3389ZM12.5 83.3389L15.7526 85.6671L24.3844 73.6085L21.1318 71.2803L17.8793 68.952L9.24743 81.0106L12.5 83.3389ZM21.1318 71.2803L24.0555 68.5504C19.2898 63.4465 16.5 56.9852 16.5 50.0049L12.5 50.0049L8.5 50.0049C8.50004 59.2009 12.1936 67.5688 18.2082 74.0102L21.1318 71.2803ZM12.5 50.0049L16.5 50.0049C16.5001 34.2345 31.0423 20.6719 49.999 20.6719V16.6719V12.6719C27.5357 12.6719 8.50014 28.9565 8.5 50.0049L12.5 50.0049Z" fill="white" />
    </svg>
  )
}

export default KioskChatHistoryButton
